import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button } from "reactstrap";
import { MaterialReactTable } from "material-react-table";
import { Box, IconButton } from "@mui/material";
import { MdDelete, MdLockReset } from "react-icons/md";
import dayjs from "dayjs";
import { useUsersApi } from "../../services/users";
import { AddUserModal } from "./AddUserModal";
import { ResetPasswordModal } from "./ResetPasswordModal";
import { DeleteUserModal } from "./DeleteUserModal";

const formatTime = (ms) => (ms ? dayjs(ms).format("DD MMM YYYY, HH:mm") : "Never");

// Admin-only: reached from the menu under the username, like Analytics, and
// refused by the backend to anyone else.
export const Users = () => {
  const usersApi = useUsersApi();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [adding, setAdding] = useState(false);
  // The row a dialog acts on. Kept after the dialog closes, so its text does
  // not blank out while it fades away.
  const [target, setTarget] = useState(null);
  const [dialog, setDialog] = useState(null); // "reset" | "delete" | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await usersApi.list());
      setLoadError(null);
    } catch (error) {
      setLoadError(error.message);
    }
    setLoading(false);
  }, [usersApi]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (name, user) => {
    setTarget(user);
    setDialog(name);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "email",
        header: "Email",
        Cell: ({ row }) => (
          <>
            {row.original.email}
            {row.original.isSelf ? (
              <span style={{ color: "#575757" }}> (you)</span>
            ) : null}
          </>
        ),
      },
      { accessorKey: "name", header: "Name" },
      {
        id: "role",
        header: "Role",
        // No role assigned means the app treats them as a user. Optional
        // chaining because the table renders placeholder rows while loading,
        // which have none of these fields.
        accessorFn: (user) => (user.roles?.length ? user.roles.join(", ") : "user"),
      },
      {
        id: "lastSignInAt",
        header: "Last sign-in",
        accessorFn: (user) => user?.lastSignInAt ?? 0,
        Cell: ({ row }) => formatTime(row.original.lastSignInAt),
      },
      {
        id: "createdAt",
        header: "Added",
        accessorFn: (user) => user?.createdAt ?? 0,
        Cell: ({ row }) => formatTime(row.original.createdAt),
      },
    ],
    []
  );

  return (
    <>
      <div className="row mt-5 justify-content-end mb-2">
        <div className="col-md-11">
          <div className="row">
            <div className="col-md-9" style={{ textAlign: "left" }}>
              <h5
                style={{
                  fontSize: "24px",
                  fontStyle: "normal",
                  fontWeight: 700,
                  lineHeight: "28px",
                  letterSpacing: "-0.5px",
                }}
              >
                Users
              </h5>
            </div>
            <div className="col-md-3" style={{ textAlign: "right" }}>
              <Button
                style={{
                  borderColor: "#4C44D8",
                  backgroundColor: "#4C44D8",
                  color: "#fff",
                  paddingLeft: "30px",
                  paddingRight: "30px",
                }}
                onClick={() => setAdding(true)}
                data-testid="add-user"
              >
                Add user
              </Button>
            </div>
          </div>
          {loadError ? (
            <Alert color="danger" className="mt-2" style={{ fontSize: "13px" }}>
              {loadError}
            </Alert>
          ) : null}
          <div className="row mt-2 justify-content-center">
            <div className="col-md-12">
              <MaterialReactTable
                columns={columns}
                data={users}
                state={{ isLoading: loading }}
                enableRowSelection={false}
                enableColumnOrdering={false}
                enableFullScreenToggle={false}
                enableDensityToggle={false}
                enableColumnFilters={false}
                enableTopToolbar
                enableBottomToolbar
                enableRowActions
                positionActionsColumn="last"
                enableColumnActions={false}
                initialState={{ density: "compact" }}
                muiTableBodyCellProps={{
                  sx: {
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontStyle: "normal",
                    fontWeight: "500",
                    lineHeight: "20px",
                    letterSpacing: "-0.5px",
                  },
                }}
                muiTableHeadCellProps={{
                  sx: {
                    zIndex: "auto",
                    color: "#4C44D8",
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontStyle: "normal",
                    fontWeight: "600",
                    letterSpacing: "-0.5px",
                  },
                }}
                renderRowActions={({ row }) => (
                  <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
                    <IconButton
                      color="secondary"
                      title="Reset password"
                      aria-label={"Reset password of " + row.original.email}
                      onClick={() => open("reset", row.original)}
                    >
                      <MdLockReset />
                    </IconButton>
                    {/* The backend refuses this too; see backend/users.py. */}
                    <IconButton
                      color="error"
                      title={
                        row.original.isSelf
                          ? "You cannot delete your own account"
                          : "Delete user"
                      }
                      aria-label={"Delete " + row.original.email}
                      disabled={row.original.isSelf}
                      onClick={() => open("delete", row.original)}
                    >
                      <MdDelete />
                    </IconButton>
                  </Box>
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <AddUserModal
        isOpen={adding}
        toggle={() => setAdding(false)}
        onAdded={load}
      />
      {target ? (
        <>
          <ResetPasswordModal
            isOpen={dialog === "reset"}
            user={target}
            toggle={() => setDialog(null)}
          />
          <DeleteUserModal
            isOpen={dialog === "delete"}
            user={target}
            toggle={() => setDialog(null)}
            onDeleted={load}
          />
        </>
      ) : null}
    </>
  );
};
